/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description getPaymentRecordSchema
 */
import _ from 'lodash';
import { PAYMENT_METHODS } from 'app-constants/payments';
import { createFormFieldRow } from 'components/forms/schema/eligibilityRequestFormSchema';
import { disableFutureDates } from 'utils/eligibility/eligibilityRequestForm';
import DescriptionField from 'components/payments/recordPayment/DescriptionField';
import { FHIR_ENTITY_TYPES, YEAR_MONTH_DATE_FORMAT } from '@veritable/common/constants';

const schemaKeys = {
	selectedGuarantor: 'selectedGuarantor',
	amount: 'amount',
	date: 'date',
	paymentMethod: 'paymentMethod',
	referenceId: 'referenceId',
	description: 'description',
	files: 'files',
};

const PAYMENT_FORM_FIELDS = {
	selectedGuarantor: {
		label: 'Guarantor',
		placeholder: 'Select Guarantor',
	},
	amount: {
		label: 'Collected Amount ($)',
		placeholder: '25',
	},
	date: {
		label: 'Collected On',
		placeholder: 'MM/DD/YYYY',
	},
	paymentMethod: {
		label: 'Payment Method',
		placeholder: 'Select a Payment Method',
	},
	referenceId: {
		label: 'Reference Number',
		placeholder: 'E.g.: WXI234FJCHI',
	},
	description: {
		label: 'Description',
		placeholder: 'Write a brief description of the payment',
	},
	files: {
		label: 'Upload Attachment(s)',
		placeholder: 'Choose Files',
		tooltip: 'Allowed file formats: pdf, doc, docx, rtf, txt, xls, csv, jpg, png, tiff',
	},
};

interface IGetPaymentRecordSchemaParams {
	attachmentMode?: PatientPayment.PaymentRecordAttachmentMode;
}

const getAttachmentFieldSchema = (): AnyType => ({
	[schemaKeys.files]: {
		title: PAYMENT_FORM_FIELDS.files.label,
		type: 'string',
		'x-decorator': 'FormItem',
		'x-decorator-props': { tooltip: PAYMENT_FORM_FIELDS.files.tooltip },
		'x-component': 'CloudflareUploadField',
		'x-component-props': {
			multiple: true,
			accept: ['.pdf', '.doc', '.docx', '.rtf', '.txt', '.xls', '.csv', '.jpg', '.png', '.tiff'],
			maxFileSize: null,
			placeholder: PAYMENT_FORM_FIELDS.files.placeholder,
			entityType: FHIR_ENTITY_TYPES.paymentReconciliation,
		},
	},
});

const getPaymentRecordSchema = ({ attachmentMode = 'legacy' }: IGetPaymentRecordSchemaParams = {}): AnyType => {
	return {
		title: 'Payment Record Form',
		type: 'object',
		properties: {
			formWrapper: {
				type: 'void',
				'x-component': 'div',
				'x-component-props': {
					className: 'py-4 px-6',
				},
				properties: {
					[schemaKeys.selectedGuarantor]: {
						title: PAYMENT_FORM_FIELDS.selectedGuarantor.label,
						type: 'object',
						'x-decorator': 'FormItem',
						'x-decorator-props': {
							asterisk: true,
						},
						'x-component': 'GuarantorSelect',
						'x-validator': [
							{
								message: 'Please select the guarantor',
								validator: (value: { reference?: string }, { message }: Record<'message', string>): string | null =>
									value?.reference ? null : message,
							},
						],
					},
					[schemaKeys.amount]: {
						title: PAYMENT_FORM_FIELDS.amount.label,
						type: 'number',
						'x-decorator': 'FormItem',
						'x-component': 'NumberPicker',
						'x-validator': [{ required: true, message: 'Please enter the amount' }],
						'x-component-props': {
							placeholder: PAYMENT_FORM_FIELDS.amount.placeholder,
						},
					},
					[schemaKeys.date]: {
						title: PAYMENT_FORM_FIELDS.date.label,
						type: 'string',
						'x-decorator': 'FormItem',
						'x-component': 'DatePicker',
						'x-validator': [{ required: true, message: 'Please enter the date' }],
						'x-component-props': {
							inputDateFormat: YEAR_MONTH_DATE_FORMAT,
							placeholder: PAYMENT_FORM_FIELDS.date.placeholder,
							disabledDate: disableFutureDates,
						},
					},
					...createFormFieldRow('paymentMethodRow', {
						[schemaKeys.paymentMethod]: {
							title: PAYMENT_FORM_FIELDS.paymentMethod.label,
							type: 'string',
							'x-decorator': 'FormItem',
							'x-component': 'Select',
							'x-validator': [{ required: true, message: 'Please enter the payment method' }],
							'x-component-props': {
								optionFilterProp: 'searchLabel',
								showSearch: true,
								allowClear: true,
								placeholder: PAYMENT_FORM_FIELDS.paymentMethod.placeholder,
								options: _.map(PAYMENT_METHODS, (method) => ({
									label: method.label,
									value: method.value,
								})),
							},
						},
						[schemaKeys.referenceId]: {
							title: PAYMENT_FORM_FIELDS.referenceId.label,
							type: 'string',
							'x-decorator': 'FormItem',
							'x-component': 'Input',
							'x-validator': [{ required: true, message: 'Please enter the reference ID' }],
							'x-component-props': {
								placeholder: PAYMENT_FORM_FIELDS.referenceId.placeholder,
							},
						},
					}),
					[schemaKeys.description]: {
						title: PAYMENT_FORM_FIELDS.description.label,
						type: 'string',
						'x-decorator': 'FormItem',
						'x-component': DescriptionField,
						'x-validator': [{ required: true, message: 'Please enter the description' }],
					},
					...(attachmentMode === 'legacy' ? getAttachmentFieldSchema() : {}),
				},
			},
		},
	};
};

export { schemaKeys };
export default getPaymentRecordSchema;
